import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { findCompanyAcrossPortfolios } from "../data/portfolios";
import { fmtCur } from "../utils/format";
import Tooltip from "../components/Tooltip";
import LeadershipGauges from "../components/LeadershipGauges";

const C = {
  bg: "#0a0a0a",
  card: "#111",
  border: "rgba(255,255,255,0.08)",
  gold: "#C9A84C",
  teal: "#00D4C8",
  violet: "#7C5CFC",
  white: "#F2F6FA",
  muted: "#888780",
  fog: "#6B7E91",
  red: "#FF3B5C",
  amber: "#E8C547",
  green: "#2EC8A0",
  grey: "#444",
};

const PATHWAY_MAP = {
  "Flight Operations": "BOT",
  "Clinical Operations": "BOT",
  "Network Operations": "BOT",
  "Network & Infrastructure": "BOT",
  "Commercial": "BORROW",
  "Customer Operations": "BORROW",
  "HR & People": "BORROW",
  "Nursing": "BORROW",
  "Digital & IT": "BUILD",
  "Technology": "BUILD",
  "Digital Health": "BUILD",
  "Digital Services": "BUILD",
  "Technology & Digital": "BUILD",
  "Engineering & MRO": "BUILD",
  "Finance": "BUILD",
  "Risk & Compliance": "BUILD",
  "Ground Services": "BOT",
  "Administration": "BORROW",
  "Retail Banking": "BOT",
  "Corporate Banking": "BORROW",
};

const PATHWAY_STYLE = {
  BOT: { color: C.teal, label: "BOT", splits: [45, 20, 25, 10] },
  BORROW: { color: C.gold, label: "BORROW", splits: [15, 40, 30, 15] },
  BUILD: { color: C.violet, label: "BUILD", splits: [20, 15, 45, 20] },
  BUY: { color: C.grey, label: "BUY", splits: [10, 15, 25, 50] },
};

const PATHWAY_TIPS = {
  BOT: "Tasks identified as AI-addressable using current-generation tools. No human oversight required at task level.",
  BORROW: "Tasks better served by fractional, interim or advisory talent than permanent headcount.",
  BUILD: "Tasks requiring internal capability development, reskilling or succession investment.",
  BUY: "Tasks requiring new external hires with capabilities not available internally.",
};


const GAP_SHARES = [0.30, 0.25, 0.20, 0.15];

function sectionStyle() {
  return {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "32px 28px",
    marginBottom: 24,
  };
}

function SectionTitle({ title, subtitle, tip }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: C.white, margin: 0 }}>
        {title}
      </h3>
      {subtitle && (
        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5, letterSpacing: "0.04em" }}>
          {subtitle} {tip && <Tooltip text={tip} />}
        </p>
      )}
    </div>
  );
}

function HBar({ label, value, maxValue, color, amount }) {
  const pct = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: C.muted }}>{label}</span>
        <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color }}>{amount}</span>
      </div>
      <div style={{ height: 12, background: "rgba(255,255,255,0.06)", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 6, transition: "width 0.5s ease" }} />
      </div>
    </div>
  );
}

function VelocityBar({ item, maxScore }) {
  const pct = maxScore > 0 ? (item.score / maxScore) * 100 : 0;
  const color = item.isTarget ? C.gold : C.grey;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <span style={{
        fontFamily: "'Inter', sans-serif", fontSize: 13, color: item.isTarget ? C.gold : "rgba(255,255,255,0.8)",
        fontWeight: item.isTarget ? 700 : 400, width: 180, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {item.name}
      </span>
      <div style={{ flex: 1, height: 14, background: "rgba(255,255,255,0.06)", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 6, transition: "width 0.5s ease" }} />
      </div>
      <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: item.isTarget ? C.gold : C.muted, width: 32, textAlign: "right", fontWeight: item.isTarget ? 700 : 400 }}>
        {item.score}
      </span>
    </div>
  );
}

function FunctionCard({ name, gap, pathway, currency }) {
  const ps = PATHWAY_STYLE[pathway] || PATHWAY_STYLE.BUILD;
  const splits = ps.splits;
  const labels = ["Bot", "Borrow", "Build", "Buy"];
  const colors = [C.teal, C.gold, C.violet, C.grey];
  return (
    <div style={{
      background: "#0d0d0d", border: `1px solid ${C.border}`, borderRadius: 10, padding: "18px 20px",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: C.white }}>{name}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", padding: "3px 8px", borderRadius: 4,
          background: `${ps.color}20`, color: ps.color, border: `1px solid ${ps.color}40`,
        }}>
          {ps.label} <Tooltip text={PATHWAY_TIPS[pathway] || ""} width={260} />
        </span>
      </div>
      <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: C.muted }}>Gap: {fmtCur(gap, currency)}</span>
      <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden" }}>
        {splits.map((s, i) => (
          <div key={i} style={{ width: `${s}%`, background: colors[i], opacity: 0.85 }} title={`${labels[i]} ${s}%`} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        {splits.map((s, i) => (
          <span key={i} style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: colors[i], opacity: 0.7 }}>
            {labels[i]} {s}%
          </span>
        ))}
      </div>
    </div>
  );
}

function Gauge({ score }) {
  const r = 80;
  const sw = 10;
  const cx = 100;
  const cy = 100;
  const startAngle = 135;
  const endAngle = 405;
  const totalAngle = endAngle - startAngle;
  const scoreAngle = startAngle + (score / 100) * totalAngle;

  function arc(start, end, radius) {
    const s = (start * Math.PI) / 180;
    const e = (end * Math.PI) / 180;
    const x1 = cx + radius * Math.cos(s);
    const y1 = cy + radius * Math.sin(s);
    const x2 = cx + radius * Math.cos(e);
    const y2 = cy + radius * Math.sin(e);
    const large = end - start > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`;
  }

  // Opportunity frame: high = most value to unlock = most urgent (red)
  const gaugeColor = score >= 70 ? C.red : score >= 40 ? C.amber : C.green;

  return (
    <svg viewBox="0 0 200 160" style={{ width: "100%", maxWidth: 260 }}>
      <path d={arc(startAngle, endAngle, r)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={sw} strokeLinecap="round" />
      {score > 0 && (
        <path d={arc(startAngle, scoreAngle, r)} fill="none" stroke={gaugeColor} strokeWidth={sw} strokeLinecap="round" />
      )}
      <text x={cx} y={cy - 4} textAnchor="middle" fill={C.gold} fontSize="42" fontFamily="'Bebas Neue', sans-serif" letterSpacing="0.04em">
        {score}
      </text>
      <text x={cx} y={cy + 16} textAnchor="middle" fill={C.muted} fontSize="12" fontFamily="'Overpass Mono', monospace">
        / 100
      </text>
    </svg>
  );
}

export default function CompanyDeepDive() {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = findCompanyAcrossPortfolios(id);

  const funcData = useMemo(() => {
    if (!company) return [];
    const fns = company.functions;
    const gap = company.asbc_gap;
    return fns.map((name, i) => {
      const share = i < GAP_SHARES.length ? GAP_SHARES[i] : (1 - GAP_SHARES.reduce((a, b) => a + b, 0)) / Math.max(fns.length - GAP_SHARES.length, 1);
      const pathway = PATHWAY_MAP[name] || "BUILD";
      return { name, gap: Math.round(gap * share), pathway };
    });
  }, [company]);

  if (!company) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, color: C.white, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36 }}>Company not found</h2>
          <button onClick={() => navigate("/dashboard")} style={{ marginTop: 16, padding: "10px 20px", background: "transparent", border: `1px solid ${C.gold}`, color: C.gold, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Back to portfolio
          </button>
        </div>
      </div>
    );
  }

  const labourCost = company.labour_cost;
  const gap = company.asbc_gap;
  const optimal = labourCost - gap;
  const ebitdaOpp = gap * 0.6;
  const estEbitda = labourCost * 0.15;
  const gapPctEbitda = estEbitda > 0 ? ((gap / estEbitda) * 100).toFixed(0) : 0;
  const recoverableMargin = gap * 0.6;
  const maxVelocity = Math.max(...(company.competitors || []).map((c) => c.score));
  const readiness = company.readiness_score;
  const readinessLabel =
    readiness >= 70
      ? "Critical opportunity \u2014 immediate action creates highest EBITDA impact"
      : readiness >= 40
      ? "Significant opportunity \u2014 structured intervention recommended"
      : "Moderate opportunity \u2014 monitor trajectory";

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.white, fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "48px 28px 0" }}>
        <button onClick={() => navigate("/dashboard")} style={{ background: "none", border: "none", color: C.gold, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 20, letterSpacing: "0.02em" }}>
          ← Back to portfolio
        </button>
        <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 52, margin: "0 0 6px", letterSpacing: "0.02em" }}>{company.name}</h1>
        <p style={{ fontSize: 14, color: C.muted, margin: "0 0 24px" }}>
          {company.sector} · {company.headcount.toLocaleString()} employees
        </p>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 32, flexWrap: "wrap", marginBottom: 40 }}>
          <div>
            <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              ASBC Gap <Tooltip text="Activity-Skills Based Costing (ASBC) prices individual tasks — not roles — across 6 workforce types: FTE, part-time, contractor, gig worker, bot/AI agent, and fractional executive. The gap is the difference between current cost and the optimal cost if tasks were routed to their ideal workforce type." />
            </p>
            <p style={{ fontSize: 32, fontWeight: 700, color: C.white, margin: 0, lineHeight: 1 }}>{fmtCur(gap, company.currency)}</p>
          </div>
          <div style={{ width: 1, height: 48, background: "rgba(255,255,255,0.12)", flexShrink: 0 }} />
          <div>
            <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              EBITDA Opportunity <Tooltip text="Estimated EBITDA uplift assuming 60% of the ASBC gap is recoverable as margin. The remaining 40% funds reinvestment into Build and Buy pathways." />
            </p>
            <p style={{ fontSize: 48, fontWeight: 700, color: C.gold, margin: 0, lineHeight: 1 }}>{fmtCur(ebitdaOpp, company.currency)}</p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 28px 96px" }}>
        {/* Panel A — Workforce Cost Architecture */}
        <div style={sectionStyle()}>
          <SectionTitle title="Workforce Cost Architecture" subtitle="How workforce spend compares to ASBC optimal structure across 6 workforce types" tip="Each role is deconstructed into tasks. Every task is priced across FTE, part-time, contractor, gig worker, bot/AI agent and fractional executive." />
          <HBar label="Current Labour Cost" value={labourCost} maxValue={labourCost} color={C.fog} amount={fmtCur(labourCost, company.currency)} />
          <HBar label="ASBC Optimal" value={optimal} maxValue={labourCost} color={C.teal} amount={fmtCur(optimal, company.currency)} />
          <HBar label="Transformation Gap" value={gap} maxValue={labourCost} color={C.red} amount={fmtCur(gap, company.currency)} />
          <div style={{ display: "flex", gap: 32, flexWrap: "wrap", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: C.muted, margin: 0 }}>
                Gap as % of estimated EBITDA <Tooltip text="Estimated EBITDA margin of 15% applied as sector benchmark. Refine with actual financials." />
              </p>
              <p style={{ fontSize: 28, fontWeight: 700, color: C.white, margin: "6px 0 0" }}>{gapPctEbitda}%</p>
            </div>
            <div>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: C.muted, margin: 0 }}>
                Recoverable as margin <Tooltip text="60% recoverability assumption based on ASBC methodology. Remaining 40% funds reinvestment in Build and Buy pathways." />
              </p>
              <p style={{ fontSize: 28, fontWeight: 700, color: C.gold, margin: "6px 0 0" }}>{fmtCur(recoverableMargin, company.currency)}</p>
            </div>
          </div>
        </div>

        {/* Panel B — Competitor Restructuring Velocity */}
        <div style={sectionStyle()}>
          <SectionTitle title="Competitor Restructuring Velocity" subtitle="Directional estimate based on public signals: job posting patterns, AI partnership announcements, workforce news, and OCF benchmark movement rates" tip="Composite score based on: AI partnership announcements (30%), workforce reduction/redeployment news (25%), digital role hiring rate (25%), OCF benchmark movement (20%)." />
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 600, color: C.gold, margin: "0 0 20px" }}>
            {company.velocity_headline}
          </p>
          {(company.competitors || [])
            .slice()
            .sort((a, b) => b.score - a.score)
            .map((c) => (
              <VelocityBar key={c.name} item={c} maxScore={maxVelocity} />
            ))}
          <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.fog, margin: "12px 0 0", letterSpacing: "0.06em" }}>
            Restructuring Velocity Score (0-100) <Tooltip text="Composite score based on: AI partnership announcements (30%), workforce reduction/redeployment news (25%), digital role hiring rate (25%), OCF benchmark movement (20%)." width={300} />
          </p>
        </div>

        {/* Panel C — 4B Opportunity Map */}
        <div style={sectionStyle()}>
          <SectionTitle title="Transformation Opportunity by Function" subtitle="Each function scored across Bot/Borrow/Build/Buy pathways using ASBC task-level pricing" tip="Click any function to see the recommended pathway breakdown. Percentages are directional estimates from public data and ASBC sector models." />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {funcData.map((f) => (
              <FunctionCard key={f.name} name={f.name} gap={f.gap} pathway={f.pathway} currency={company.currency} />
            ))}
          </div>
        </div>

        {/* Panel D — Leadership Scorecard */}
        <div style={sectionStyle()}>
          <SectionTitle title="Leadership Scorecard" subtitle="Scored from public signals: LinkedIn profiles, regulatory filings, job posting patterns, Glassdoor data, and news analysis" tip="Directional estimate — refine with data room access." />
          <LeadershipGauges scores={company.leadership_scores} companyName={company.name} />
        </div>

        {/* Panel E — Transformation Opportunity */}
        <div style={sectionStyle()}>
          <SectionTitle title="Transformation Opportunity Score" subtitle="Estimated value creation potential from workforce restructuring. Higher score = larger unrealised opportunity." tip="Composite of ASBC gap velocity (30%), leadership capability score (25%), competitor restructuring position (25%), and AI adoption signals (20%). Higher score = larger unrealised opportunity = stronger case for immediate intervention." />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Gauge score={readiness} />
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600, color: readiness >= 70 ? C.red : readiness >= 40 ? C.amber : C.green, marginTop: 12, textAlign: "center", maxWidth: 400, lineHeight: 1.5 }}>
              {readinessLabel}
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: "24px 0 0", maxWidth: 540 }}>
              {(company.readiness_drivers || []).map((d, i) => (
                <li key={i} style={{ display: "flex", gap: 10, marginBottom: 10, fontSize: 14, color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}>
                  <span style={{ color: C.gold, flexShrink: 0 }}>&#x2022;</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Activation CTA */}
        <div style={{
          background: C.card,
          border: `1px solid ${C.gold}`,
          borderRadius: 12,
          padding: 32,
          marginBottom: 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}>
          <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, letterSpacing: "0.14em", color: C.gold, textTransform: "uppercase", margin: "0 0 10px", fontWeight: 600 }}>
            NEXT STEP
          </p>
          <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: C.white, margin: "0 0 12px", letterSpacing: "0.02em" }}>
            Activate the Signal Room <Tooltip text="Opens the full reAImagination platform pre-populated with this company's workforce data — Signal Room, Blueprint Cockpit, Org Intelligence, The Stage and Showcase. Share the link directly with the company's CHRO or People leader." />
          </h3>
          <p style={{ fontSize: 14, color: C.muted, margin: "0 0 24px", maxWidth: 520, lineHeight: 1.6 }}>
            Generate a pre-populated workforce intelligence view for {company.name} and share it directly with their leadership team. They'll see their Signal Room, Blueprint, and AI transformation opportunity — ready in seconds.
          </p>
          <button
            onClick={() => window.open(company.demo_url, "_blank")}
            style={{
              padding: "14px 28px",
              background: C.gold,
              color: C.bg,
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              letterSpacing: "0.02em",
              width: "100%",
              maxWidth: 400,
            }}
          >
            Open Signal Room for {company.name} →
          </button>
          <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: C.muted, marginTop: 14, marginBottom: 0, lineHeight: 1.5 }}>
            Opens the full reAImagination experience — Signal Room, Blueprint Cockpit, Org Intelligence, The Stage and Showcase.
          </p>
        </div>
      </div>
    </div>
  );
}
